import React from 'react';
import Icon from '../../../components/AppIcon';

const MacroBar = ({ label, current, target, color }) => {
  const pct = target > 0 ? Math.min((current / target) * 100, 100) : 0;
  const over = target > 0 && current > target;
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className={`text-xs font-semibold ${over ? 'text-destructive' : 'text-foreground'}`}>
          {Math.round(current)}<span className="text-muted-foreground font-normal">/{Math.round(target)}g</span>
        </span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${over ? 'bg-destructive' : color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const MacroDashboard = ({ dailyTotals, targets }) => {
  const calPct = targets?.calories > 0 ? Math.min((dailyTotals?.calories / targets?.calories) * 100, 100) : 0;
  const calOver = targets?.calories > 0 && dailyTotals?.calories > targets?.calories;

  const getCalColor = () => {
    if (calOver) return 'text-destructive';
    if (calPct >= 90) return 'text-warning';
    return 'text-primary';
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="Target" size={16} className="text-primary" />
        </div>
        <h3 className="text-sm font-semibold text-foreground">Daily Macros</h3>
      </div>
      {/* Calorie Ring */}
      <div className="flex items-center gap-4 mb-4 p-3 bg-muted/30 rounded-lg">
        <div className="relative w-16 h-16 flex-shrink-0">
          <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
            <circle cx="32" cy="32" r="26" fill="none" stroke="currentColor" strokeWidth="6" className="text-muted" />
            <circle
              cx="32" cy="32" r="26" fill="none"
              stroke="currentColor" strokeWidth="6"
              strokeDasharray={`${2 * Math.PI * 26}`}
              strokeDashoffset={`${2 * Math.PI * 26 * (1 - calPct / 100)}`}
              strokeLinecap="round"
              className={calOver ? 'text-destructive' : 'text-primary'}
              style={{ transition: 'stroke-dashoffset 0.5s ease' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-xs font-bold ${getCalColor()}`}>{Math.round(calPct)}%</span>
          </div>
        </div>
        <div className="flex-1">
          <p className="text-xs text-muted-foreground mb-0.5">Calories</p>
          <p className={`text-xl font-bold ${getCalColor()}`}>{Math.round(dailyTotals?.calories)}</p>
          <p className="text-xs text-muted-foreground">of {Math.round(targets?.calories)} kcal target</p>
          {calOver && (
            <p className="text-xs text-destructive mt-0.5">
              +{Math.round(dailyTotals?.calories - targets?.calories)} over
            </p>
          )}
        </div>
      </div>
      {/* Macro Bars */}
      <div className="space-y-3">
        <MacroBar label="Protein" current={dailyTotals?.protein} target={targets?.protein} color="bg-blue-500" />
        <MacroBar label="Carbohydrates" current={dailyTotals?.carbs} target={targets?.carbs} color="bg-amber-500" />
        <MacroBar label="Fats" current={dailyTotals?.fats} target={targets?.fats} color="bg-orange-500" />
      </div>
      {/* Remaining */}
      <div className="mt-4 pt-3 border-t border-border grid grid-cols-3 gap-2 text-center">
        {[
          { label: 'Protein left', value: Math.max(0, targets?.protein - dailyTotals?.protein), unit: 'g', color: 'text-blue-400' },
          { label: 'Carbs left', value: Math.max(0, targets?.carbs - dailyTotals?.carbs), unit: 'g', color: 'text-amber-400' },
          { label: 'Fats left', value: Math.max(0, targets?.fats - dailyTotals?.fats), unit: 'g', color: 'text-orange-400' },
        ]?.map(item => (
          <div key={item?.label} className="bg-muted/30 rounded-lg p-2">
            <p className={`text-sm font-bold ${item?.color}`}>{Math.round(item?.value)}{item?.unit}</p>
            <p className="text-xs text-muted-foreground leading-tight">{item?.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MacroDashboard;
